/**
 * Everything the ticket sheet reads about one ticket, in one hook.
 *
 * It is the case file's data layer, lifted whole and without its chrome: the
 * open gate, the runs roll-up (polled while one is live), the stage's bound
 * document, the plan's questions and the answers saved against them, the
 * merge facts while it is in Review, the gate history and the activity for the
 * timeline. It refetches on the same signals the case file did: a run flipping
 * (`useRunLiveFlip`), the item changing anywhere (`useWorkItemEntityChange`),
 * and a poll that saw a run move.
 *
 * Answers are saved on the server as they are given (spec 2026-11-02, "The
 * plan a person approves is the only contract"): each pick, typed answer,
 * Skip, change and Undo posts one input (`answerPlanQuestion`), Reeve writes
 * it into the plan as a new version, and the gate read says where each input
 * stands (`planInputs`) and which version the plan is at (`planVersion`).
 * Nothing waits in the browser for Approve.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Page, PlanInputView, WorkItem, WorkItemGate, WorkItemPageRole } from "@yagni/shared";
import { isLiveRun, isStatedQuestion, planQuestionHold, readJobGatePayload } from "@yagni/shared";
import {
  answerPlanQuestion,
  GateResolveError,
  useOpenWorkItemGate,
  type PlanAnswerBody,
} from "../tracker/useWorkItemGate.js";
import { useOpenWorkItemPendingAction } from "../tracker/useWorkItemPendingAction.js";
import { useWorkItemActivity } from "../hooks/useWorkItemActivity.js";
import { useWorkItemComments } from "../hooks/useWorkItemComments.js";
import { LIVE_RUN_POLL_MS, useLiveItem, useWorkItemRuns } from "../work/useWorkItemRuns.js";
import { useRunLiveFlip } from "../work/useRunLiveFlip.js";
import { useWorkItemEntityChange } from "../work/useWorkItemEntityChange.js";
import { useMergeAccess } from "../work/MergeFooter.js";
import { useReviewFindings } from "../work/ReviewFindings.js";
import { useWorkRunView } from "../work/RunSurface.js";
import {
  readGatePlanChecks,
  readGatePlanQuestions,
  readGateReeveRead,
  readPlanQuestionList,
  readPlanQuestions,
  usePlanRow,
  type PlanQuestionRow,
} from "../work/planQuestions.js";
import { clearStagedPlanAnswers, readStagedPlanAnswers } from "../work/stagedPlanAnswers.js";
import { pipelineStageOf } from "../work/workStage.js";
import { captureUiError } from "../sentry.js";
import { onPlanAnswerFromRail } from "../lib/planAnswerBridge.js";
import { PLAN_ANSWER_FROM_YAGNI } from "../work/workCopy.js";

interface LinkedPage {
  role: WorkItemPageRole;
  page: { id: string; title: string; pageType?: string; updatedAt?: string };
}

/**
 * Which bound page IS the work at this stage: the proposal before a plan
 * exists, the plan from planning through review, the outcome once done. Only
 * roles that are bound are chosen.
 */
export function stageRoleFor(
  item: WorkItem,
  roles: readonly WorkItemPageRole[],
): WorkItemPageRole | null {
  if (item.kind === "review") return null;
  const stage = pipelineStageOf(item);
  const order: Partial<Record<string, WorkItemPageRole[]>> = {
    proposed: ["proposal", "plan"],
    done: ["outcome", "plan", "proposal"],
  };
  const wanted = order[stage] ?? ["plan", "proposal"];
  return wanted.find((role) => roles.includes(role)) ?? null;
}

/** A read the sheet can do without: a failure is reported and reads as nothing. */
async function getJson<T>(url: string, operation: string): Promise<T | null> {
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error(`${operation} failed (${res.status})`);
    return (await res.json()) as T;
  } catch (err) {
    captureUiError(err, { area: "Work", operation });
    return null;
  }
}

/** The stage's document, read once per page and refetched when the item moves. */
function useStageDocument(
  item: WorkItem,
  refreshKey: number,
): {
  role: WorkItemPageRole | null;
  page: Page | null;
  planPage: Page | null;
  loading: boolean;
} {
  const [pages, setPages] = useState<LinkedPage[]>([]);
  const [docs, setDocs] = useState<Map<string, Page>>(new Map());
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void getJson<LinkedPage[]>(
      `/api/work-items/${encodeURIComponent(item.id)}/pages`,
      "read_ticket_pages",
    ).then(async (rows) => {
      if (cancelled) return;
      const list = Array.isArray(rows) ? rows : [];
      setPages(list);
      const wanted = list.filter(
        (entry) =>
          entry.role === "plan" ||
          entry.role ===
            stageRoleFor(
              item,
              list.map((row) => row.role),
            ),
      );
      const loaded = await Promise.all(
        wanted.map(
          async (entry) =>
            [
              entry.page.id,
              await getJson<Page>(
                `/api/pages/${encodeURIComponent(entry.page.id)}`,
                "read_ticket_page",
              ),
            ] as const,
        ),
      );
      if (cancelled) return;
      setDocs(new Map(loaded.flatMap(([id, page]) => (page ? [[id, page] as const] : []))));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // The item's phase and gate decide which document is current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.phase, item.currentGateId, refreshKey]);
  const role = stageRoleFor(
    item,
    pages.map((entry) => entry.role),
  );
  const bound = role ? pages.find((entry) => entry.role === role) : undefined;
  const planBound = pages.find((entry) => entry.role === "plan");
  return {
    role,
    page: bound ? (docs.get(bound.page.id) ?? null) : null,
    planPage: planBound ? (docs.get(planBound.page.id) ?? null) : null,
    loading,
  };
}

/** An input that answers one question; at most one is live per question. */
export function isPlanAnswer(input: PlanInputView): boolean {
  return (input.kind === "answer" || input.kind === "reeve_answer") && input.questionId != null;
}

/** Reeve is still writing it into the plan. */
export function isApplying(input: PlanInputView): boolean {
  return input.status === "queued" || input.status === "applying";
}

/** It could not be written into the plan, or needs a new plan. */
export function isUnapplied(input: PlanInputView): boolean {
  return input.status === "failed" || input.status === "needs_start_over";
}

/** How often the sheet re-reads the gate while Reeve writes an input in. */
export const PLAN_INPUT_POLL_MS = 4_000;

/**
 * An answer the sheet has sent and the server has not confirmed yet, so the
 * line shows at once. Null is an Undo on its way. Dropped when the route
 * answers with every input as it stands.
 */
type PendingAnswers = { gateId: string | null; byQuestion: Record<string, PlanInputView | null> };
const NO_PENDING: Record<string, PlanInputView | null> = {};

function pendingInput(
  question: PlanQuestionRow,
  answer: string,
  why: string,
  skipped: boolean,
): PlanInputView {
  return {
    id: `pending-${question.id}`,
    kind: "answer",
    questionId: question.id,
    question: question.text,
    body: answer,
    why,
    skipped,
    byUserId: null,
    status: "queued",
    planVersionNumber: null,
    changeSummary: null,
    error: null,
    createdAt: new Date().toISOString(),
  };
}

/** The server's inputs with the answers still on their way laid over them. */
export function withPendingAnswers(
  inputs: readonly PlanInputView[],
  pending: Record<string, PlanInputView | null>,
): PlanInputView[] {
  const ids = Object.keys(pending);
  if (ids.length === 0) return [...inputs];
  const kept = inputs.filter(
    (input) => !(isPlanAnswer(input) && ids.includes(input.questionId ?? "")),
  );
  return [...kept, ...ids.flatMap((id) => (pending[id] ? [pending[id]!] : []))];
}

export function useTicket(
  item: WorkItem,
  /** Changes when the pull request's review or fix round moves, so the merge facts follow. */
  mergeRefreshKey = "",
) {
  const {
    gate,
    planVersion,
    planInputs: serverInputs,
    setPlanInputs,
    isLoading: gateLoading,
    error: gateError,
    refetch: refetchGate,
  } = useOpenWorkItemGate(item.id);
  const { open: pendingAction, refetch: refetchPendingAction } = useOpenWorkItemPendingAction(
    item.id,
  );
  const onRunsMoved = useRef<() => void>(() => {});
  const {
    runs,
    loading: runsLoading,
    error: runsError,
    reload: reloadRuns,
    refresh: refreshRuns,
  } = useWorkItemRuns(item.id, () => onRunsMoved.current());
  const liveItem = useLiveItem(item, runs, runsLoading);
  const { activity, refresh: refreshActivity } = useWorkItemActivity(item.id);
  const { comments, reload: reloadComments } = useWorkItemComments(item.id);
  const [docRefresh, setDocRefresh] = useState(0);
  const doc = useStageDocument(item, docRefresh);

  const [gateHistory, setGateHistory] = useState<WorkItemGate[]>([]);
  useEffect(() => {
    let cancelled = false;
    void getJson<{ gates?: WorkItemGate[] }>(
      `/api/work-items/${encodeURIComponent(item.id)}/gates`,
      "read_gate_history",
    ).then((body) => {
      if (!cancelled) setGateHistory(Array.isArray(body?.gates) ? body.gates : []);
    });
    return () => {
      cancelled = true;
    };
  }, [item.id, item.currentGateId, item.phase]);

  const refreshAround = useCallback(() => {
    window.dispatchEvent(new CustomEvent("tickets.invalidate"));
    void refetchGate();
    void refetchPendingAction();
    refreshActivity();
    void reloadComments();
    setDocRefresh((n) => n + 1);
  }, [refetchGate, refetchPendingAction, refreshActivity, reloadComments]);
  const refreshAll = useCallback(() => {
    reloadRuns();
    refreshAround();
  }, [reloadRuns, refreshAround]);
  useEffect(() => {
    onRunsMoved.current = refreshAround;
  }, [refreshAround]);
  const onLiveFlip = useCallback(() => {
    reloadRuns();
    void refetchGate();
    void refetchPendingAction();
  }, [reloadRuns, refetchGate, refetchPendingAction]);
  useRunLiveFlip(item.id, onLiveFlip);
  // The item names a run the roll-up has never held: it started after the
  // roll-up was read (a plan started with the sheet open) and its frame was
  // lost. The roll-up polls only once it holds a live run, so read it again
  // in the background, now and on the live poll's clock until it holds the
  // run; a failed read is retried on the next tick, never given up on.
  const currentRunId = item.currentRunId ?? null;
  const rollUpHoldsCurrent = currentRunId == null || runs.some((run) => run.id === currentRunId);
  useEffect(() => {
    if (runsLoading || rollUpHoldsCurrent) return;
    refreshRuns();
    const timer = setInterval(refreshRuns, LIVE_RUN_POLL_MS);
    return () => clearInterval(timer);
  }, [runsLoading, rollUpHoldsCurrent, currentRunId, refreshRuns]);
  // A re-read that fails while the sheet still cannot name the live run is
  // the NEARM-526 state again, so it is reported, once per run named.
  const runsErrorReportedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!runsError || rollUpHoldsCurrent || runsErrorReportedFor.current === currentRunId) return;
    runsErrorReportedFor.current = currentRunId;
    captureUiError(runsError, { area: "Work", operation: "read_runs_for_current_run" });
  }, [runsError, rollUpHoldsCurrent, currentRunId]);
  useWorkItemEntityChange(item.id, refreshAll);
  useEffect(() => {
    if (gateError) captureUiError(gateError, { area: "Work", operation: "read_open_gate" });
  }, [gateError]);

  /* ── the plan's questions, and the answers saved against them ── */
  const atPlanGate = gate?.kind === "approve_execution_plan";
  const gateQuestions = useMemo(() => readGatePlanQuestions(gate), [gate]);
  // A new version (an answer applied, an Ask change accepted) re-reads the
  // plan page, and the plan row below, so the plan, its counts and its files
  // are the version's (the stale counts after an accepted edit).
  const versionId = planVersion?.id ?? null;
  const seenVersion = useRef<string | null>(versionId);
  useEffect(() => {
    if (seenVersion.current === versionId) return;
    const moved = seenVersion.current !== null;
    seenVersion.current = versionId;
    if (moved) setDocRefresh((n) => n + 1);
  }, [versionId]);
  // The plan row carries the plan's shape (files, steps, checks) as well as
  // the questions an older gate did not copy onto its payload.
  const planRow = usePlanRow(
    item.id,
    gate?.planId ?? item.latestPlanId ?? null,
    Boolean(doc.planPage),
    versionId,
  );
  const questions: PlanQuestionRow[] = useMemo(() => {
    if (!atPlanGate) return [];
    const structured =
      gateQuestions.length > 0 ? gateQuestions : readPlanQuestionList(planRow?.questions);
    return readPlanQuestions(structured, doc.planPage?.content ?? null);
  }, [atPlanGate, gateQuestions, planRow, doc.planPage]);
  const reeveRead = useMemo(() => readGateReeveRead(gate), [gate]);
  const planChecks = useMemo(() => readGatePlanChecks(gate), [gate]);

  const gateId = gate?.id ?? null;
  const [pending, setPending] = useState<PendingAnswers>({ gateId: null, byQuestion: {} });
  const pendingHere = pending.gateId === gateId ? pending.byQuestion : NO_PENDING;
  const [answerError, setAnswerError] = useState<string | null>(null);
  useEffect(() => {
    setAnswerError(null);
  }, [gateId]);
  // What was said at the gate, as the server holds it, with what is still on
  // its way laid over it.
  const planInputs = useMemo(
    () => (atPlanGate ? withPendingAnswers(serverInputs ?? [], pendingHere) : []),
    [atPlanGate, serverInputs, pendingHere],
  );
  const answers = useMemo(() => planInputs.filter(isPlanAnswer), [planInputs]);
  const applyingCount = planInputs.filter(isApplying).length;
  const failedCount = planInputs.filter(isUnapplied).length;

  // One input, posted: the line shows at once (`pending`), the route's
  // answer replaces it with every input as it stands, and the gate is read
  // again for the version. A refusal is said on the sheet.
  const send = useCallback(
    async (
      body: PlanAnswerBody,
      optimistic?: { questionId: string; input: PlanInputView | null },
    ) => {
      if (!gateId) return;
      if (optimistic) {
        setPending((prev) => ({
          gateId,
          byQuestion: {
            ...(prev.gateId === gateId ? prev.byQuestion : {}),
            [optimistic.questionId]: optimistic.input,
          },
        }));
      }
      setAnswerError(null);
      const settle = () => {
        if (!optimistic) return;
        setPending((prev) => {
          if (prev.gateId !== gateId) return prev;
          const { [optimistic.questionId]: _sent, ...rest } = prev.byQuestion;
          return { gateId, byQuestion: rest };
        });
      };
      try {
        const { inputs } = await answerPlanQuestion(item.id, gateId, body);
        setPlanInputs(inputs);
        settle();
        void refetchGate();
      } catch (err) {
        settle();
        if (!(err instanceof GateResolveError && err.status < 500)) {
          captureUiError(err, { area: "Work", operation: "sheet_answer_plan_question" });
        }
        setAnswerError(
          err instanceof Error && err.message
            ? err.message
            : "That answer was not saved. Try again.",
        );
        void refetchGate();
      }
    },
    [gateId, item.id, refetchGate, setPlanInputs],
  );
  // `skipped`: the person let Reeve choose (Skip took the recommendation). The
  // plan records it as Reeve's choice, never as the person's own answer.
  const answerQuestion = useCallback(
    (question: PlanQuestionRow, answer: string, why: string, skipped = false) => {
      const trimmed = answer.trim();
      if (!trimmed) return;
      void send(
        { questionId: question.id, answer: trimmed, why, ...(skipped ? { skipped: true } : {}) },
        { questionId: question.id, input: pendingInput(question, trimmed, why, skipped) },
      );
    },
    [send],
  );
  /** Undo: the answer is withdrawn, and the plan goes back to what it was without it. */
  const undoAnswer = useCallback(
    (questionId: string) => {
      void send({ questionId, answer: null }, { questionId, input: null });
    },
    [send],
  );
  /** Retry an input that could not be written into the plan. */
  const retryInput = useCallback((inputId: string) => void send({ inputId, retry: true }), [send]);
  /** Keep an answer that could not be written into the plan as its decision line. */
  const keepAsDecision = useCallback(
    (inputId: string) => void send({ inputId, decisionOnly: true }),
    [send],
  );
  /** Drop a note or a reply that could not be written into the plan. */
  const dismissInput = useCallback(
    (inputId: string) => void send({ inputId, answer: null }),
    [send],
  );
  // While Reeve writes an input in, the gate is read again on a short clock:
  // the entity change says when it lands, and this catches a missed one. A
  // quiet read: a blip keeps the gate, so the clock keeps running.
  useEffect(() => {
    if (!atPlanGate || applyingCount === 0) return;
    const timer = setInterval(() => void refetchGate({ quiet: true }), PLAN_INPUT_POLL_MS);
    return () => clearInterval(timer);
  }, [atPlanGate, applyingCount, refetchGate]);
  // The one move from browser staging: answers this browser kept for an open
  // gate that has nothing saved are posted once, then cleared. Once per gate.
  const moved = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!atPlanGate || !gateId || serverInputs === null || moved.current.has(gateId)) return;
    moved.current.add(gateId);
    const kept = readStagedPlanAnswers(gateId);
    if (kept.length === 0) return;
    if (serverInputs.length > 0) {
      clearStagedPlanAnswers(gateId);
      return;
    }
    void (async () => {
      let lost = false;
      for (const entry of kept) {
        try {
          await answerPlanQuestion(item.id, gateId, {
            questionId: entry.questionId,
            answer: entry.answer,
            why: entry.why,
            ...(entry.skipped ? { skipped: true } : {}),
          });
        } catch (err) {
          // A question the plan no longer asks is dropped; anything else is
          // kept for the next load.
          if (!(err instanceof GateResolveError && err.status < 500)) lost = true;
          captureUiError(err, {
            area: "Work",
            operation: "sheet_move_kept_plan_answers",
            severity: "warning",
          });
        }
      }
      if (!lost) clearStagedPlanAnswers(gateId);
      void refetchGate();
    })();
  }, [atPlanGate, gateId, serverInputs, item.id, refetchGate]);
  // A reply the reader chose in the dock as the answer to one of these
  // questions is saved exactly like a picked option.
  useEffect(() => {
    if (!atPlanGate) return;
    return onPlanAnswerFromRail(({ questionId, answer }) => {
      const question = questions.find((entry) => entry.id === questionId);
      if (question) answerQuestion(question, answer, PLAN_ANSWER_FROM_YAGNI);
    });
  }, [atPlanGate, questions, answerQuestion]);
  const answeredIds = useMemo(
    () =>
      new Set(
        questions
          .filter((question) => answers.some((entry) => entry.questionId === question.id))
          .map((question) => question.id),
      ),
    [questions, answers],
  );
  // What Reeve states on the plan (its decisions, and the facts it checked or
  // planned on), and the questions a person still settles. Approving accepts
  // the first; the second hold Approve until each has an answer (the approve
  // route holds the same rule).
  const statedQuestions = useMemo(() => questions.filter(isStatedQuestion), [questions]);
  const askedQuestions = useMemo(
    () => questions.filter((question) => !isStatedQuestion(question)),
    [questions],
  );
  const questionHold = useMemo(
    () =>
      planQuestionHold(
        questions.map((question) => ({
          answered: answeredIds.has(question.id),
          stated: isStatedQuestion(question),
        })),
      ),
    [questions, answeredIds],
  );
  /* ── Review: the merge facts, and a held review's drafted findings ── */
  const terminal = item.phase === "done" || item.phase === "dismissed" || item.phase === "split";
  const liveRun = runs.some(isLiveRun);
  const inReviewWithPr =
    !terminal &&
    item.kind !== "review" &&
    Boolean(item.prUrl) &&
    pipelineStageOf(item) === "review";
  // Wright's own asks about its pull request read them too: the ask says when
  // the pull request conflicts with its base.
  const wrightPrAsk = gate ? readJobGatePayload(gate.payload)?.act === "push_fix" : false;
  const merge = useMergeAccess(
    item.id,
    inReviewWithPr && (!gate || wrightPrAsk) && !liveRun,
    item.prUrl ?? null,
    mergeRefreshKey,
  );
  const review = useReviewFindings(item.id, gate?.kind === "review_result");

  /* ── the attempt a stop kept ── */
  const attemptPhase = !terminal && (item.phase === "needs_input" || item.phase === "executing");
  const { view: runView, failed: runViewFailed } = useWorkRunView(item.id, attemptPhase);

  return {
    gate,
    gateLoading,
    gateError,
    refetchGate,
    pendingAction,
    runs,
    runsLoading,
    reloadRuns,
    liveItem,
    activity,
    comments,
    gateHistory,
    doc,
    questions,
    reeveRead,
    planChecks,
    statedQuestions,
    askedQuestions,
    questionHold,
    planVersion,
    planInputs,
    answers,
    answerQuestion,
    undoAnswer,
    retryInput,
    keepAsDecision,
    dismissInput,
    answerError,
    applyingCount,
    failedCount,
    answeredIds,
    merge,
    review,
    planRow,
    runView,
    runViewFailed,
    refreshAll,
  };
}

export type TicketState = ReturnType<typeof useTicket>;
